import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Anchor, Eye, EyeOff, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resending, setResending] = useState(false);

  const isSignup = mode === "signup";

  const handleResendVerification = async () => {
    if (!email) return toast.error("Please enter your email address first");
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      toast.success(data.message || "Verification email sent! Check your inbox.");
      setShowResendVerification(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Please fill in all fields");
    if (isSignup && password !== confirmPassword) return toast.error("Passwords do not match");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");

    setLoading(true);
    setShowResendVerification(false);
    try {
      if (isSignup) {
        await signup(email, password, confirmPassword);
        toast.success("Account created! Check your email to verify your account.");
        navigate("/login");
      } else {
        await login(email, password);
        toast.success("Welcome back!");
        navigate("/dashboard");
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail || "Something went wrong. Please try again.";
      if (status === 403 && !isSignup) {
        setShowResendVerification(true);
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-8">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Anchor className="h-7 w-7 text-primary" />
          <span className="font-bold text-xl text-[#0a1628]" style={{ fontFamily: 'Manrope' }}>CalmOceanPrep</span>
        </Link>

        <Card className="border shadow-sm" data-testid="auth-card">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
              {isSignup ? "Create Account" : "Welcome Back"}
            </CardTitle>
            <CardDescription>
              {isSignup ? "Sign up to start practicing for your MMD orals" : "Log in to continue your preparation"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="auth-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {isSignup && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="auth-confirm-password-input"
                  />
                </div>
              )}
              <Button type="submit" className="w-full h-10" disabled={loading} data-testid="auth-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isSignup ? "Create Account" : "Log In"}
              </Button>
            </form>

            {showResendVerification && !isSignup && (
              <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="resend-verification-block">
                <p className="text-xs text-amber-800 mb-2">Your email is not verified yet. Click below to receive a new verification link.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-amber-300 text-amber-800 hover:bg-amber-100"
                  onClick={handleResendVerification}
                  disabled={resending}
                  data-testid="resend-verification-btn"
                >
                  {resending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Mail className="h-3 w-3 mr-1" />}
                  Resend Verification Email
                </Button>
              </div>
            )}

            <div className="mt-4 text-center text-sm">
              {isSignup ? (
                <p className="text-muted-foreground">
                  Already have an account?{" "}
                  <Link to="/login" className="text-primary font-medium hover:underline" data-testid="switch-to-login">Log in</Link>
                </p>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Don't have an account?{" "}
                    <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="switch-to-signup">Sign up</Link>
                  </p>
                  <Link to="/forgot-password" className="text-muted-foreground hover:text-primary text-xs mt-2 block" data-testid="forgot-password-link">
                    Forgot Password?
                  </Link>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
